import { useLearningObjectivesStore } from "store/modules/learningObjectivesStore";
import { createDefaultManager } from "./defaultManager";
import { clamp, remove } from "lodash";
import {
  MAX_PERIODS,
  DEFAULT_PERIOD_LENGTH,
  DEFAULT_PERIOD_OBJECT,
  DEFAULT_PERIOD_NAME,
} from "models/learning-objectives/learningObjectivesConstants";
import { Period } from "models/learning-objectives/periodModel";
import { onMounted, ref } from "vue";
import { getWeekNumber } from "common/dateHelpers";

const createPeriodManager = () => {
  const {
    weeks,
    isInitialized,
    deltaState,
    showCurrentWeekIndicator,
    fetchAndSetWeeks,
    startDelta,
    endDelta,
  } = createDefaultManager();

  const periods = ref([]);
  const maxPeriodsReached = ref(false);

  const init = async ({ force = false } = {}) => {
    if (isInitialized.value && !force) {
      return;
    }

    await fetchAndSetWeeks();
    await fetchPeriods();

    isInitialized.value = true;
  };

  const fetchPeriods = async () => {
    const learningObjectivesStore = useLearningObjectivesStore();
    const { fetchPeriods } = learningObjectivesStore;

    const fetchedPeriods = await fetchPeriods();

    addPeriod(fetchedPeriods, true);

    return fetchedPeriods;
  };

  const updatePlanning = async () => {
    await Promise.all(periods.value.map((period) => period.upsertPeriod()));
  };

  const addPeriod = (data, cleanPeriods = false) => {
    if (cleanPeriods) {
      periods.value = [];
    }

    if (Array.isArray(data)) {
      data.forEach((period) => periods.value.push(new Period(period)));
    } else {
      periods.value.push(new Period(data));
    }

    // Sort periods by start week index
    periods.value.sort(
      (a, b) => a.planningMeta.weekIndexes[0] - b.planningMeta.weekIndexes[0],
    );

    hasReachedMaxPeriods();
  };

  const createTemporaryPeriod = ({
    duration,
    start_week_index,
    school_year,
  }) => {
    if (maxPeriodsReached.value) {
      throw new Error("Maximaal aantal periodes bereikt");
    }

    const startWeek = weeks.value[start_week_index];
    const endWeekIndex = clamp(
      start_week_index + duration - 1,
      0,
      start_week_index + DEFAULT_PERIOD_LENGTH - 1,
    );
    const endWeek = weeks.value[endWeekIndex];

    const period = {
      ...DEFAULT_PERIOD_OBJECT,
      start_week: startWeek.week,
      start_year: startWeek.year,
      end_week: endWeek.week,
      end_year: endWeek.year,
      school_year,
      name: DEFAULT_PERIOD_NAME,
      temporary: true,
      duration,
    };

    addPeriod(period);

    return period;
  };

  const removePeriod = async (periodId) => {
    const period = findPeriodById(periodId);

    // Delete period from the server
    await period.deletePeriod();

    // Remove from the local store
    remove(periods.value, ({ id }) => id === periodId);

    hasReachedMaxPeriods();
  };

  const findPeriodById = (periodId = null) => {
    if (!periodId) {
      return null;
    }

    return periods.value.find(({ id }) => id === periodId);
  };

  const hasReachedMaxPeriods = () => {
    maxPeriodsReached.value = periods.value.length >= MAX_PERIODS;

    return maxPeriodsReached.value;
  };

  const findClosestPeriod = (_period, changeInColumns, handleSide) => {
    if (!_period) {
      return null;
    }

    // Determine the starting point based on the handle side
    const currentIndex =
      handleSide === "start"
        ? _period.planningMeta.weekIndexes[0]
        : _period.planningMeta.weekIndexes[
            _period.planningMeta.weekIndexes.length - 1
          ];

    // Calculate the new position based on the drag distance
    const newIndex = currentIndex + changeInColumns;
    const isMovingRight = Math.sign(changeInColumns) > 0;

    let closestPeriod = null;
    let closestDistance = Infinity;

    for (const period of periods.value) {
      if (period.id === _period.id) {
        continue;
      }

      const periodStartIndex = period.planningMeta.weekIndexes[0];
      const periodEndIndex =
        period.planningMeta.weekIndexes[
          period.planningMeta.weekIndexes.length - 1
        ];

      if (handleSide === "start" && !isMovingRight) {
        // Moving left from start, find closest period on the left
        if (newIndex <= periodEndIndex && periodEndIndex < currentIndex) {
          const distance = currentIndex - periodEndIndex;

          if (distance < closestDistance) {
            closestDistance = distance;
            closestPeriod = period;
          }
        }
      } else if (handleSide === "end" && isMovingRight) {
        // Moving right from end, find closest period on the right
        if (newIndex >= periodStartIndex && periodStartIndex > currentIndex) {
          const distance = periodStartIndex - currentIndex;

          if (distance < closestDistance) {
            closestDistance = distance;
            closestPeriod = period;
          }
        }
      }
    }

    return closestPeriod;
  };

  const calculateEndIndex = (
    endWeekIndex,
    changeInColumns,
    period,
    handleSide,
    isExpanding,
  ) => {
    let snappedPeriod = null;
    let endIndex = endWeekIndex;

    if (changeInColumns === 0) {
      return { endIndex, snappedPeriod };
    }

    // Negative, end week is moving to the left
    if (!isExpanding) {
      snappedPeriod = periods.value.find(
        (_period) =>
          _period.planningMeta.weekIndexes.includes(endWeekIndex + 1) &&
          _period.id !== period.id,
      );

      const endIndex = clamp(
        endWeekIndex + changeInColumns,
        period.planningMeta.weekIndexes[0],
        period.planningMeta.weekIndexes[
          period.planningMeta.weekIndexes.length - 1
        ],
      );

      return { endIndex, snappedPeriod };
    }

    // Sign is positive, end week is moving to the right
    endIndex = clamp(
      endWeekIndex + changeInColumns,
      period.planningMeta.weekIndexes[
        period.planningMeta.weekIndexes.length - 1
      ],
      weeks.value.length - 1,
    );

    snappedPeriod = findClosestPeriod(period, changeInColumns, handleSide);

    if (!snappedPeriod) {
      return { endIndex, snappedPeriod };
    }

    endIndex = clamp(
      endWeekIndex + changeInColumns,
      period.planningMeta.weekIndexes[
        period.planningMeta.weekIndexes.length - 1
      ],
      snappedPeriod.planningMeta.weekIndexes[
        snappedPeriod.planningMeta.weekIndexes.length - 1
      ] - 1,
    );

    return { endIndex, snappedPeriod };
  };

  const calculateStartIndex = (
    startWeekIndex,
    changeInColumns,
    period,
    handleSide,
    isExpanding,
  ) => {
    let snappedPeriod = null;
    let startIndex = startWeekIndex;

    if (changeInColumns === 0) {
      return { startIndex, snappedPeriod };
    }

    // Sign is positive, start week is moving to the right
    if (isExpanding && changeInColumns > 0) {
      snappedPeriod =
        periods.value.find(
          (_period) =>
            _period.planningMeta.weekIndexes.includes(startWeekIndex - 1) &&
            _period.id !== period.id,
        ) || null;

      startIndex = clamp(
        startWeekIndex + changeInColumns,
        0,
        period.planningMeta.weekIndexes[
          period.planningMeta.weekIndexes.length - 1
        ],
      );

      return { startIndex, snappedPeriod };
    }

    // Sign is negative, start week is moving to the left
    startIndex = clamp(
      startWeekIndex + changeInColumns,
      0,
      period.planningMeta.weekIndexes[
        period.planningMeta.weekIndexes.length - 1
      ],
    );

    snappedPeriod = findClosestPeriod(period, changeInColumns, handleSide);

    if (snappedPeriod) {
      startIndex = clamp(
        startWeekIndex + changeInColumns,
        snappedPeriod.planningMeta.weekIndexes[0] + 1,
        period.planningMeta.weekIndexes[
          period.planningMeta.weekIndexes.length - 1
        ],
      );

      return { startIndex, snappedPeriod };
    }

    return { startIndex, snappedPeriod };
  };

  const updateDelta = (period, changeInColumns, handleSide, deltaX) => {
    if (!period || !deltaState[period.id]) {
      return;
    }

    const { id } = period;

    let { startWeekIndex, endWeekIndex, deltaStart } = deltaState[id];

    // Check if the period is expaning or contracting
    const isExpanding = deltaX > deltaStart;

    // Update previous deltaStart with the current deltaX
    deltaState[id].deltaStart = deltaX;

    if (handleSide === "end") {
      const { endIndex, snappedPeriod } = calculateEndIndex(
        endWeekIndex,
        changeInColumns,
        period,
        handleSide,
        isExpanding,
      );

      const { week, year } = weeks.value[endIndex];

      period.updateEndWeek(week, year);

      deltaState[id].endWeekIndex = endIndex;

      if (snappedPeriod) {
        const { week, year } = weeks.value[endIndex + 1];

        snappedPeriod.updateStartWeek(week, year);
      }
    }

    if (handleSide === "start") {
      const { startIndex, snappedPeriod } = calculateStartIndex(
        startWeekIndex,
        changeInColumns,
        period,
        handleSide,
        isExpanding,
      );

      let { week, year } = weeks.value[startIndex];

      period.updateStartWeek(week, year);

      deltaState[id].startWeekIndex = startIndex;

      if (snappedPeriod) {
        let { week, year } = weeks.value[startIndex - 1];

        snappedPeriod.updateEndWeek(week, year);
      }
    }
  };

  return {
    periods,
    weeks,
    maxPeriodsReached,
    isInitialized,
    deltaState,
    showCurrentWeekIndicator,
    init,
    updatePlanning,
    createTemporaryPeriod,
    removePeriod,
    findPeriodById,
    startDelta,
    endDelta,
    updateDelta,
  };
};

export const periodManager = ref(createPeriodManager());

export const usePeriodManager = () => {
  const isInitialized = ref(false);

  onMounted(async () => {
    await periodManager.value.init();

    isInitialized.value = true;
  });

  return { periodManager, isInitialized };
};
